import React, { useState, useEffect } from "react";
import WidgetCard from "@/components/controls/WidgetCard";
import { useTranslation } from "react-i18next";
import StageAverageDurationChart from "@/components/graphs/StageAverageDuration/StageAverageDurationChart";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import Tabs from "@/components/controls/Tabs";
import Dropdown from "@/components/controls/Dropdown/Dropdown";
import { UserData } from "@/entities/users/types";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import ComparisonChart from "@/components/graphs/ComparisonChart/ComparisonChart";
import Text from "@/components/ui/text";
import DurationDrilldownModal from "@/components/layouts/DurationDrilldownModal/DurationDrilldownModal";
import { DealMilestone } from "@/pages/deals/types";
import { getColorForMilestoneByType } from "@/components/shared/Milestones";
import { useDropdownClosing } from "@/services/hooks/useDropdownClosing";
import {
  AverageTimeSpentDirectReportsResponse,
  SettingsPeriod
} from "@/entities/reporting/types";
import { periodsDropdownProps } from "@/components/layouts/ConversionRates/helpers";
import {
  periodEnumToString,
  periodStringToEnum
} from "@/entities/reporting/helpers";
import { StageDurationBreadcrumbsProvider } from "./StageDurationBreadcrumbsContext";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import { camelCase, groupBy, map } from "lodash";

export type StageAverageDurationChartDataType = {
  id: string;
  label: string;
  start: number;
  duration: number;
  milestone: DealMilestone;
  team: number;
  org: number;
  industry?: number;
};

export type StageAverageDurationPropsType = {
  chartData: {
    [key: string]: StageAverageDurationChartDataType[];
  };
  userData?: UserData;
  period: SettingsPeriod;
  periodSelect: (period: SettingsPeriod) => void;
  isLoading?: boolean;
  isAE: boolean;
  drillDownHidden?: boolean;
  directReportsData?: AverageTimeSpentDirectReportsResponse;
};
export const STAGES = "stages";
export const MILESTONES = "milestones";

const StageAverageDuration: React.FC<StageAverageDurationPropsType> = ({
  chartData,
  userData,
  period,
  periodSelect,
  isLoading,
  isAE,
  drillDownHidden = false,
  directReportsData
}) => {
  const { t } = useTranslation();
  const showIndustryValue = useFeatureFlag(FeatureFlags.INDUSTRY_VALUE).enabled;
  const [selectedTab, setSelectedTab] = useState(STAGES);
  const [selectedStageIndex, setSelectedStageIndex] = useState(-1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { isDropdownClosing } = useDropdownClosing();

  const stageGroups = groupBy(chartData["stages"], "milestone");

  const renderMilestone = (
    milestone: StageAverageDurationChartDataType,
    index: number
  ) => (
    <div className="flex gap-2 grow items-center" key={index}>
      <Avatar className="!size-5 !rounded-lg !text-[10px] !text-on-surface">
        <AvatarFallback
          style={{
            backgroundColor: getColorForMilestoneByType(milestone.milestone)
          }}
        >
          {index + 1}
        </AvatarFallback>
      </Avatar>
      <Tooltip>
        <TooltipTrigger className="flex items-center">
          <Text as="div" variant="bodySecondary">
            {milestone.label}
          </Text>
        </TooltipTrigger>
        <TooltipContent
          data-test="stage-tooltip"
          className="flex flex-col gap-2 max-w-48"
        >
          <Text as="div" variant="labelSecondary">
            {milestone.label}
          </Text>
          <Text as="div" variant="body">
            {t(
              `stageAverageDuration.descriptions.${camelCase(milestone.label)}`
            )}
          </Text>
        </TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger className="text-tertiary text-sm flex items-center">
          {`${stageGroups[milestone.milestone].length} ${t("stageAverageDuration.stages")}`}
        </TooltipTrigger>
        <TooltipContent data-test="stages-tooltip">
          <Text as="div" variant="body" className="flex flex-col gap-1">
            {map(stageGroups[milestone.milestone], (stage, index) => (
              <div key={index}>{stage.label}</div>
            ))}
          </Text>
        </TooltipContent>
      </Tooltip>
    </div>
  );

  const renderStage = (
    stage: StageAverageDurationChartDataType,
    index: number
  ) => (
    <div className="flex gap-2 grow items-center" key={index}>
      <Avatar className="!size-5 !rounded-lg !text-[10px] !text-on-surface">
        <AvatarFallback
          style={{
            backgroundColor: getColorForMilestoneByType(
              stage.milestone || undefined
            )
          }}
        >
          {index + 1}
        </AvatarFallback>
      </Avatar>
      <Text as="div" variant="bodySecondary">
        {stage.label}
      </Text>
    </div>
  );

  useEffect(() => {
    const currentData = chartData[selectedTab];
    if (
      selectedStageIndex !== -1 &&
      (!currentData || selectedStageIndex >= currentData.length)
    ) {
      setSelectedStageIndex(-1);
    }
  }, [chartData, selectedTab, selectedStageIndex]);

  const renderStageInfo = () => {
    const currentData = chartData[selectedTab];
    const selectedStage = currentData?.[selectedStageIndex];

    return selectedStageIndex === -1 || !selectedStage ? (
      <div className="h-full flex justify-center items-center">
        <Text
          as="div"
          variant="bodySecondary"
          className="text-wrap text-center"
        >{`${t("stageAverageDuration.clickMessageFirst")} ${t(`stageAverageDuration.${selectedTab === STAGES ? "stage" : "milestone"}`)} ${t("stageAverageDuration.clickMessageSecond")}`}</Text>
      </div>
    ) : (
      <>
        <div className="flex flex-col justify-between space-y-1">
          <div className="flex flex-col gap-2">
            <Text
              data-test={`${selectedStage.id}-label`}
              as="div"
              variant="lgSubtitleH3"
            >
              {selectedStage.label}
            </Text>
            <Text
              data-test={`${selectedStage.id}-duration`}
              variant="title3H1"
              className="text-center"
            >{`${selectedStage.duration} ${t("stageAverageDuration.weeks")}`}</Text>
          </div>
        </div>
        <div className="max-h-36 h-full pb-12">
          <Text as="div" variant="bodySecondary">
            {t("comparison.title")}
          </Text>
          <ComparisonChart
            datasets={[
              {
                name: t(`comparison.titles.you`),
                value: selectedStage.duration || 0
              },
              {
                name: t(`comparison.titles.team`),
                value: selectedStage.team || 0
              },
              {
                name: t(`comparison.titles.organization`),
                value: selectedStage.org || 0
              },
              {
                name: t(`comparison.titles.industry`),
                value: selectedStage.industry || 0
              }
            ]}
            userData={userData}
            unit="w"
            hideIndustry={!showIndustryValue || selectedTab === STAGES}
          />
        </div>
      </>
    );
  };

  const handleBarClick = (index: number) => {
    setSelectedStageIndex(index !== selectedStageIndex ? index : -1);
  };

  const multiplyBy = selectedTab === STAGES ? 4 : 5;

  const height = `${Math.max(multiplyBy * chartData[selectedTab].length, 25)}rem`;

  return (
    <>
      <WidgetCard
        cardHeight={height}
        title={t("stageAverageDuration.title")}
        isLoading={isLoading}
        feedBackButtonIdContext="duration"
        infoTooltip={t("stageAverageDuration.info")}
        isDrillDownHidden={isAE || drillDownHidden}
        drillDownOnClick={() => setIsModalOpen(!isModalOpen)}
        tabs={
          <Tabs
            options={{
              [STAGES]: t("stageAverageDuration.stages"),
              [MILESTONES]: t("stageAverageDuration.milestones")
            }}
            defaultValue={STAGES}
            valueSelected={(value) => {
              setSelectedStageIndex(-1);
              setSelectedTab(value);
            }}
          />
        }
        filter={
          <Dropdown
            {...periodsDropdownProps(t)}
            defaultValue={periodEnumToString(period)}
            valueSelected={(value) => periodSelect(periodStringToEnum(value))}
          />
        }
      >
        <>
          <div className="flex-grow min-w-0 relative flex">
            <div className="w-64 min-w-48 shrink-0" />
            <div className="grow max-w-full overflow-x-auto">
              <StageAverageDurationChart
                chartData={chartData[selectedTab]}
                selectedStageIndex={selectedStageIndex}
              />
            </div>
            <div className="absolute inset-0 flex flex-col gap-4">
              <Text
                as="div"
                variant="bodySecondary"
                className="flex w-60 text-end justify-end pr-2"
              >
                {t("stageAverageDuration.weeks")}
              </Text>
              <div className="flex flex-col h-full mb-1 gap-[7px]">
                {map(chartData[selectedTab], (element, index) => (
                  <div
                    key={index}
                    onClick={() => handleBarClick(index)}
                    data-test={`${element.id}-element`}
                    className="grow flex transition-colors duration-200 hover:bg-slate-950/20 cursor-pointer"
                  >
                    {selectedTab === STAGES
                      ? renderStage(element, index)
                      : renderMilestone(element, index)}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="flex flex-col w-[12.5rem] pr-4 min-w-10 justify-between">
            {renderStageInfo()}
          </div>
        </>
      </WidgetCard>
      <StageDurationBreadcrumbsProvider
        leadUser={userData}
        period={period}
        enableDirectReportsData={isModalOpen}
        initialDirectReportsData={directReportsData}
      >
        <DurationDrilldownModal
          isOpen={isModalOpen && !isDropdownClosing}
          setIsOpen={setIsModalOpen}
          chartData={chartData}
          directReportsData={directReportsData}
          mainSelectedStageId={
            chartData[selectedTab]?.[selectedStageIndex]?.id ?? ""
          }
          mainSelectedTab={selectedTab}
          period={period}
          periodSelect={periodSelect}
        />
      </StageDurationBreadcrumbsProvider>
    </>
  );
};

export default StageAverageDuration;
